import { Card } from '../../../components/Card/Card';
import { useContext, useState } from 'react';
import { PatientRecord } from '../../../shared/types/types';
import { Button } from '../../../components/Button/Button';
import { Edit } from '../../../icons/Edit';
import './PatientInfo.scss';
import { LoadingSpinner, mediumSpinner } from '../../../components/common/loader/LoadingSpinner';
import { ServerError } from './serverError/ServerError';
import { DisplayPatientInfo } from './display/DisplayPatientInfo';
import { useNavigate } from 'react-router-dom';
import { ExternalUserVerifyContext } from 'src/shared/contexts/ExternalAprrovalContext';
import { BannerMessageContext } from 'src/shared/contexts/BannerMessageContext';
import { v4 as uuidv4 } from 'uuid';
import { WAS_BACK_END_API_URL } from 'src/constants';
import { fetchAuthenticated } from 'src/controllers';
import { PopupMessageContext } from 'src/shared/contexts/PopupMessageContext';

export interface UpdatePatientInfoProps {
  patientData?: Partial<PatientRecord>;
}

export interface PatientInfoProps {
  patientData?: PatientRecord;
  isLoading: boolean;
  profilePhotoString: string;
  error: string;
  token: string | null;
}

export function PatientInfo({ patientData, isLoading, error, profilePhotoString, token }: PatientInfoProps) {
  const navigate = useNavigate();
  const { checkAuthorization } = useContext(ExternalUserVerifyContext);
  const { addBannerMessage } = useContext(BannerMessageContext);
  const { addPopupMessage } = useContext(PopupMessageContext);

  const [expanded, setIsExpanded] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  const handlePatientInfoExpand = () => {
    setIsExpanded(!expanded);
  };

  const handleSuspendTreatmentClick = async () => {
    // do something to invalidate treatment
    setCheckingOut(true);
    if (!checkAuthorization) return;
    let result;
    try {
      result = await checkAuthorization();
    } catch (error) {
      addBannerMessage({
        id: uuidv4(),
        type: 'error',
        message: 'Incorrect Access Credentials',
      });
    }
    if (result?.verified) {
      const url = WAS_BACK_END_API_URL + `/batonTreatmentReturn/${patientData?._id}`;

      const result = await fetchAuthenticated(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      if (result?.ok) {
        addBannerMessage({
          id: uuidv4(),
          message: 'Treatment Suspended Successfully.',
          type: 'success',
        });
        setCheckingOut(false);
        navigate(`/`);
      } else {
        addPopupMessage({
          id: uuidv4(),
          message: 'An error occurred when trying to suspend treatment for this patient.',
          header: 'Unapproval',
        });
      }
    }
    setCheckingOut(false);
  };

  const body = error ? (
    <ServerError error={error} />
  ) : isLoading ? (
    <div className="loading-container">
      <LoadingSpinner width={mediumSpinner} height={mediumSpinner} />
    </div>
  ) : (
    <div className="patient-info-container">
      <div className="patient-info-and-avatar">
        {
          <DisplayPatientInfo
            archived={patientData?.archived}
            dateOfBirth={patientData?.encrypt_dateOfBirth}
            givenName={patientData?.encrypt_givenName}
            familyName={patientData?.encrypt_familyName}
            gender={patientData?.encrypt_gender}
            medicalId={patientData?.encrypt_medicalId}
            patientType={patientData?.encrypt_type}
            profilePhotoString={profilePhotoString}
            middleName={patientData?.encrypt_middleName}
          />
        }
      </div>
      <div className="edit-controls">
        {!checkingOut ? (
          <Button label={'Suspend Treatment'} onClick={() => handleSuspendTreatmentClick()} className="edit-button" icon={<Edit />} iconPlacement="left" />
        ) : (
          <LoadingSpinner width={mediumSpinner} height={mediumSpinner} />
        )}
      </div>
    </div>
  );

  return (
    <div className="patient-info-container">
      <Card title={'PATIENT INFORMATION'} body={expanded ? body : <></>} onExpand={handlePatientInfoExpand} expandable={true} expanded={expanded} />
    </div>
  );
}
